import { appOrigin } from '../lib/hosts.ts';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import type { GridPage } from '../lib/grid.ts';
import { LevelBadge, ymd, type PublicNotice } from '../lib/notices.tsx';
import { useReveal, useScrollFx } from './useReveal.ts';
import { CONTACT_EMAIL, useTitle } from './site.ts';

/** 사이트 메인 (CR-031 내용 · CR-041 제품 페이지 문법). 문구는 기획서 01 목표를 사실대로 옮긴 것이며 과장하지 않는다 */
const JOBS: Array<[string, string]> = [
  ['규정 제·개정과 이력', '내부관리계획과 하위 지침을 조·항·호 단위로 관리하고, 개정 이력을 남기며 HTML/PDF 로 출력합니다.'],
  ['공개 문서 게시', '개인정보 처리방침 같은 공개 문서를 공개 URL 로 게시하고 이전 버전을 열람할 수 있게 합니다.'],
  ['개정 검토 일정', '법률·ISMS-P·ISO 기준이 바뀌면 영향받는 규정을 찾아 검토 일정을 만듭니다.'],
  ['증적 관리', '스크린샷 위주의 증적을 수집하고 버전과 유효기간을 관리합니다. 승인된 버전은 덮어쓰지 않습니다.'],
  ['자체점검', '법률에 정한 점검 주기를 관리하고 결과를 기록하며 미흡사항의 개선을 추적합니다.'],
  ['위탁·제공·국외이전 대장', '수탁사와 위탁 계약·점검·교육, 제3자 제공과 국외이전을 대장으로 관리합니다.'],
  ['물리·무선보안 대장', '보호구역·출입 기록과 무선 AP·비인가 탐지 기록을 대장으로 관리합니다.'],
  ['결재 워크플로', '수탁사 점검 결과, 규정 개정, 재위탁처럼 확인이 필요한 건을 결재선에 올려 진행 상태를 추적합니다.'],
];
const MODULES: Array<[string, string, string]> = [
  ['M0', '통제항목 마스터', 'ISMS-P·개인정보보호법·ISO 기준을 통제항목으로 등록하고 규정·증적·점검을 여기에 연결'],
  ['M1', '규정 관리', '내부관리계획·지침의 제·개정, 버전 이력, HTML/PDF 출력, 공개 문서 게시'],
  ['M2', '증적 관리', '증적 항목·버전·유효기간, 증적 요청과 검토'],
  ['M3', '자체점검', '점검 회차·체크리스트 응답·미흡사항 개선 과제'],
  ['M4', '위탁·제공·국외이전', '수탁사, 위탁 계약·점검·교육, 제공·국외이전 대장'],
  ['M5', '개인정보 파일 대장', '개인정보 파일과 처리 현황'],
  ['M6', '물리보안', '보호구역·출입 대장'],
  ['M7', '무선보안', '무선 AP·비인가 탐지 대장'],
  ['M8', '일정·알림', '의무 일정, 할 일, 기한 알림'],
  ['M9', '대시보드', '일정·담당자·지연 상태 현황'],
  ['M10', '사용자·권한·이력', '역할·범위 기반 권한, 접속기록, 모든 데이터의 변경 이력'],
  ['M11', '테넌트·플랫폼 관리', '조직 단위 격리, 도메인·브랜딩, 플랫폼 공지'],
  ['M12', '결재 엔진', '건별로 유연하게 정하는 결재선과 결재함'],
];
const STEPS = ['조직(테넌트) 생성', '관리자 초대·로그인', '부서·역할·권한 설정', '통제항목·규정 정리', '증적·점검·대장 운영'];

export function HomePage() {
  useTitle();
  useReveal();
  useScrollFx();
  const notices = useQuery({ queryKey: ['site-notices'], queryFn: () => api<GridPage<PublicNotice>>('/public/notices/grid?pageSize=5&sort=-createdAt') /* 목록 규약 최소 5건, 화면엔 3건 */, staleTime: 60_000, retry: false });
  const login = `${appOrigin()}/login`;
  return (
    <div>
      {/* ① 히어로 */}
      <section className="s-dark s-hero">
        <div className="s-wrap-wide">
          <div className="s-aurora" aria-hidden />
          <div className="s-hero-copy">
            <p className="s-eyebrow s-in s-d1">PRISM</p>
            <h1 className="s-h1 s-in s-d2">정보보호·개인정보보호 업무를{' '}<br className="s-br" />한 시스템에서.</h1>
            <p className="s-lead center s-in s-d3">규정·증적·자체점검·위탁 대장·결재를 통제항목 기준으로 연결해 관리하는 멀티테넌트 SaaS. 조직 단위로 데이터가 격리되고, 모든 변경은 이력으로 남습니다.</p>
            <div className="s-links s-in s-d4"><a href={login}>로그인</a><a href={`mailto:${CONTACT_EMAIL}`}>도입 문의</a></div>
            <div className="s-tags s-in s-d5">{['ISMS-P', '개인정보보호법', 'ISO 27001'].map((t) => <span key={t}>{t}</span>)}</div>
          </div>
          <div className="s-mock s-mock-in" aria-hidden>
            <div className="s-mock-bar"><b>PRISM</b><i>개인정보 및 정보보안관리체계</i><span className="r"><span>화면 분할</span><span>조직</span><span>관리자</span><span>내 계정</span></span></div>
            <div className="s-mock-tabs">{['홈', '일정', '규정', '증적', '점검', '위탁 관리', '대장', '결재'].map((t) => <span key={t} className={t === '위탁 관리' ? 'on' : ''}>{t}</span>)}</div>
            <div className="s-mock-body">
              <div className="s-mock-side"><div className="h">위탁 관리</div><div className="i">수탁사</div><div className="i on">위탁 업무</div><div className="i">제3자 제공</div><div className="i">국외이전</div></div>
              <div className="s-mock-main">
                <div className="t">위탁 업무</div>
                <div className="s-mock-cards"><div><small>진행 중 위탁 업무</small><strong>12</strong></div><div><small>처리방침 미반영</small><strong className="warn">2</strong></div><div><small>계약 만료 30일 내</small><strong>1</strong></div><div><small>연 1회 점검 미이행</small><strong className="warn">3</strong></div></div>
                <div className="s-mock-rows">
                  <div><span>위탁 업무</span><span>수탁사</span><span>부서</span><span>상태</span><span>최근 점검</span></div>
                  <div><span>배송업무위탁</span><span>(주)에이셉익스프레스</span><span>개발팀</span><span><em>진행</em></span><span>2026-08-30</span></div>
                  <div><span>이메일 발송 대행</span><span>클라우드메일(주)</span><span>개발팀</span><span><em>진행</em></span><span>2026-07-12</span></div>
                  <div><span>고객센터 운영</span><span>디버로지스틱스</span><span>경영지원팀</span><span><em className="w">결재 중</em></span><span>—</span></div>
                  <div><span>급여 계산 대행</span><span>페이롤코리아</span><span>경영지원팀</span><span><em>진행</em></span><span>2026-06-02</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ② 한눈에 */}
      <section className="s-section s-center">
        <div className="s-wrap">
          <div className="s-stats">
            <div className="s-stat" data-reveal><b data-count="8">8</b><span>업무 영역</span><small>규정부터 결재까지</small></div>
            <div className="s-stat" data-reveal="2"><b data-count="13">13</b><span>모듈</span><small>하나의 통제항목 허브로 연결</small></div>
            <div className="s-stat" data-reveal="3"><b data-count="3">3</b><span>대응 기준</span><small>ISMS-P · 개인정보보호법 · ISO 27001</small></div>
          </div>
        </div>
      </section>

      {/* ③ 하는 일 */}
      <section className="s-section s-soft">
        <div className="s-wrap-wide">
          <h2 className="s-h2" data-reveal>반복되는 일을<br />한곳에서.</h2>
          <p className="s-lead" data-reveal="2">정보보호·개인정보보호 업무에서 매년 되풀이되는 일을 시스템이 기억하고, 담당자는 확인만 하면 됩니다.</p>
          <div className="s-bento">
            {JOBS.map(([t, d], i) => <div key={t} className={`s-card ${i < 2 ? 'big' : ''}`} data-reveal={String((i % 3) + 1)}><span className="k">{String(i + 1).padStart(2, '0')}</span><h3>{t}</h3><p>{d}</p></div>)}
          </div>
        </div>
      </section>

      {/* ④ 모듈 */}
      <section className="s-section s-dark">
        <div className="s-wrap-wide">
          <h2 className="s-h2 on-dark" data-reveal>13개 모듈.<br />하나의 통제항목 허브.</h2>
          <p className="s-lead" data-reveal="2">규정·증적·점검·대장은 모두 통제항목에 연결됩니다. 기준이 바뀌면 영향받는 곳이 바로 보입니다.</p>
          <div className="s-modules">
            {MODULES.map(([c, n, d], i) => <div key={c} className="s-module" data-reveal={String((i % 3) + 1)}><code>{c}</code><h3>{n}</h3><p>{d}</p></div>)}
          </div>
        </div>
      </section>

      {/* ⑤ 대응 기준 */}
      <section className="s-section">
        <div className="s-wrap-wide">
          <h2 className="s-h2" data-reveal>기준을 통제항목으로.</h2>
          <p className="s-lead" data-reveal="2">세 기준의 요구사항을 통제항목 마스터로 등록하고, 규정·증적·점검이 어느 항목을 충족하는지 연결해 관리합니다.</p>
          <div className="s-cols3">
            <div data-reveal><h3>ISMS-P</h3><p>정보보호 및 개인정보보호 관리체계 인증 기준. 인증 심사에 필요한 규정·증적·점검 기록을 항목별로 모읍니다.</p></div>
            <div data-reveal="2"><h3>개인정보보호법</h3><p>안전성 확보조치 기준과 내부관리계획·위탁·제공·국외이전 등 법이 정한 의무를 대장과 일정으로 관리합니다.</p></div>
            <div data-reveal="3"><h3>ISO 27001</h3><p>정보보안 경영시스템 통제항목과 연결해 같은 증적을 여러 기준에 재사용합니다.</p></div>
          </div>
        </div>
      </section>

      {/* ⑥ 도입 흐름 */}
      <section className="s-section s-soft">
        <div className="s-wrap-wide">
          <h2 className="s-h2" data-reveal>도입은 다섯 단계.</h2>
          <p className="s-lead" data-reveal="2">조직은 테넌트 단위로 분리되어 운영됩니다.</p>
          <ol className="s-steps">{STEPS.map((s, i) => <li key={s} data-reveal={String((i % 4) + 1)}><b>{String(i + 1).padStart(2, '0')}</b><span>{s}</span></li>)}</ol>
          <p className="s-note" data-reveal>도입 절차와 계약은 <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> 로 문의하세요.</p>
        </div>
      </section>

      {/* ⑦ 공지 */}
      <section className="s-section">
        <div className="s-wrap">
          <h2 className="s-h2" data-reveal>공지사항</h2>
          <ul className="s-notices" data-reveal="2">
            {notices.data?.rows.slice(0, 3).map((n) => <li key={n.id}><LevelBadge level={n.level} /><Link to={`/notices/${n.id}`}>{n.title}</Link><time>{ymd(n.createdAt)}</time></li>)}
            {notices.data && notices.data.rows.length === 0 && <li style={{ color: 'var(--s-muted)' }}>게시 중인 공지가 없습니다.</li>}
          </ul>
          <Link to="/notices" className="s-more">전체 보기 ›</Link>
        </div>
      </section>
    </div>
  );
}
